import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createClubCoreClient, isApiError, type Api, type TokenStore } from "@clubcore/api";
import { createContext, useContext, useState, type ReactNode } from "react";
import { ClubSelection } from "./club-selection";
import { followHostClub } from "./host-club";
import { LicenseStore } from "./license";
import { SessionEvents } from "./session-events";
import { BrowserTokenStore } from "./token-store";

/** Everything the app talks to the outside world through. Tests pass fakes. */
export interface Services {
  api: Api;
  tokenStore: TokenStore;
  clubSelection: ClubSelection;
  sessionEvents: SessionEvents;
  queryClient: QueryClient;
  license: LicenseStore;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Client errors will not fix themselves; only retry transient failures.
        retry: (failures, error) => failures < 2 && !(isApiError(error) && error.status >= 400 && error.status < 500),
      },
    },
  });
}

export function createBrowserServices(): Services {
  const tokenStore = new BrowserTokenStore();
  const clubSelection = new ClubSelection();
  const sessionEvents = new SessionEvents();
  const license = new LicenseStore();
  const api = createClubCoreClient({
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? "/api/v1",
    tokenStore,
    getClub: () => clubSelection.getSlug(),
    onUnauthorized: () => sessionEvents.expire(),
    onResponse: (info) => {
      license.observe(info);
      followHostClub(info.headers, clubSelection);
    },
  });
  return { api, tokenStore, clubSelection, sessionEvents, queryClient: createQueryClient(), license };
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return (
    <ServicesContext.Provider value={services}>
      <QueryClientProvider client={services.queryClient}>{children}</QueryClientProvider>
    </ServicesContext.Provider>
  );
}

export function useServices(): Services {
  const value = useContext(ServicesContext);
  if (!value) throw new Error("ServicesProvider is missing");
  return value;
}

export const useApi = () => useServices().api;

/** Stable for the life of the app; handy for useState initialisers. */
export function useStableServices(factory: () => Services): Services {
  return useState(factory)[0];
}
